import { InvoiceCreationLocation, InvoiceLocationStatus } from '../invoices/invoice-location.service';

export type DeliveryLocationIssue = Exclude<InvoiceLocationStatus, 'Captured'>;
export type CapturedDeliveryLocation = Required<InvoiceCreationLocation> & { captureStatus: 'Captured' };

const messages: Record<DeliveryLocationIssue, string> = {
  PermissionDenied: 'Location access is blocked. Allow location for this website and for your browser in phone settings, then check again.',
  PositionUnavailable: 'Your phone could not find its location. Turn on Location Services, move somewhere with better reception and try again.',
  Timeout: 'Location was not received in time. Choose Allow if the browser is asking, keep this page open and try again.',
  Unsupported: 'This browser cannot provide location. Open Farm2Me in an up-to-date Chrome, Edge or Safari browser.',
  InsecureContext: 'Location requires a secure connection. Open Farm2Me using HTTPS.',
  InvalidData: 'The location reading was invalid or out of date. Check your phone clock and try again.'
};
export class DeliveryLocationError extends Error {
  constructor(public readonly issue: DeliveryLocationIssue) { super(messages[issue]); this.name = 'DeliveryLocationError'; }
}

// A check never starts/ends delivery. Each actual action requests another fresh
// reading. No coordinates are logged, cached or written to browser storage.
export function captureDeliveryLocation(signal?: AbortSignal): Promise<CapturedDeliveryLocation> {
  if (signal?.aborted) return Promise.reject(new DOMException('Location check cancelled.', 'AbortError'));
  if (!window.isSecureContext) return Promise.reject(new DeliveryLocationError('InsecureContext'));
  if (!navigator.geolocation) return Promise.reject(new DeliveryLocationError('Unsupported'));
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (reading?: CapturedDeliveryLocation, failure?: Error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(deadline);
      signal?.removeEventListener('abort', cancel);
      if (reading) resolve(reading); else reject(failure);
    };
    const cancel = () => finish(undefined, new DOMException('Location check cancelled.', 'AbortError'));
    // Allow time to answer the permission prompt, separately from the browser's
    // 20-second position timeout. Late callbacks cannot submit an activity.
    const deadline = window.setTimeout(() => finish(undefined, new DeliveryLocationError('Timeout')), 60000);
    signal?.addEventListener('abort', cancel, { once: true });
    try {
      navigator.geolocation.getCurrentPosition(position => {
        const { latitude, longitude, accuracy } = position.coords;
        const age = Date.now() - position.timestamp;
        if (![latitude, longitude, accuracy, position.timestamp].every(Number.isFinite) ||
            Math.abs(latitude) > 90 || Math.abs(longitude) > 180 || accuracy < 0 || accuracy > 999999999 ||
            age > 120000 || age < -30000) {
          finish(undefined, new DeliveryLocationError('InvalidData')); return;
        }
        finish({ captureStatus: 'Captured', latitude, longitude, accuracyMeters: accuracy,
          capturedAtUtc: new Date(position.timestamp).toISOString() });
      }, error => finish(undefined, new DeliveryLocationError(error.code === 1 ? 'PermissionDenied' :
        error.code === 3 ? 'Timeout' : 'PositionUnavailable')),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 });
    } catch { finish(undefined, new DeliveryLocationError('PositionUnavailable')); }
  });
}
